// Pointer/pen → sim commands. Coalesced samples, tap-vs-drag, no game logic
// beyond deciding *which* sim command a gesture means.
import type { Aircraft, Vec, World } from '../sim/index.ts';
import { assignPath, planeAt, previewPath, toggleHold } from '../sim/index.ts';
import { InkStroke, MIN_SPACING } from './ink.ts';
import { OneEuroFilter2D } from './oneEuro.ts';

// Aircraft pick radius (logical px), both numbers kept together: a thumb or
// finger is imprecise and needs a generous target; a stylus is precise
// enough that a tighter radius reads better and avoids grabbing a neighbour.
const RADIUS_TOUCH = 38;
const RADIUS_PEN = 26;

export interface PointerInputOptions {
  getWorld(): World | null;
  /** current `viewScale(cssW,cssH)` — client px ÷ this = logical px */
  getViewScale(): number;
  /** notified with the aircraft id currently being dragged, or null once released — lets the renderer brighten its path guide */
  onDragChange?(planeId: number | null): void;
  /** notified right after a path is committed or a hold is toggled — first-use signal for onboarding, not a per-frame hook */
  onCommand?(kind: 'path' | 'hold', plane: Aircraft): void;
  // A finger that goes down away from every aircraft has nothing to command.
  // Rather than swallow the gesture, it is handed on as ink for the app to
  // draw and fade — no sim state is touched, so these are the only two hooks:
  // one opens a stroke, the other extends it with a filtered point in logical px.
  onDoodleStart?(): void;
  onDoodlePoint?(p: Vec): void;
  // The finger as an instrument (src/app/sound.ts): every sample of a route
  // being drawn that moved at least MIN_SPACING from the last one, with the
  // finger's speed over that gap, and how the stroke ended — snapped to a
  // runway or a pad, left free, a tap that toggled a hold, or cancelled.
  onInkPoint?(p: Vec, speedPxPerSec: number): void;
  onInkEnd?(result: 'runway' | 'pad' | 'free' | 'hold' | 'cancel'): void;
}

interface Drag { plane: Aircraft; stroke: InkStroke; pointerId: number; ink: { x: number; y: number; tMs: number } | null }
/** A stroke drawn on empty sky. No aircraft, no sim command — only the 1€
 *  filter, so the line under the finger is as steady as a real route's. */
interface Doodle { pointerId: number; filter: OneEuroFilter2D }

/** Attach Pointer Events to `canvas`. Returns a cleanup function that removes all listeners. */
export function attachPointerInput(canvas: HTMLCanvasElement, opts: PointerInputOptions): () => void {
  canvas.style.touchAction = 'none';
  let drag: Drag | null = null;
  let doodle: Doodle | null = null;

  const toLogical = (clientX: number, clientY: number, vs: number): Vec => {
    const rect = canvas.getBoundingClientRect();
    return { x: (clientX - rect.left) / vs, y: (clientY - rect.top) / vs };
  };

  /** every position the pointer really passed through since the last event,
   *  falling back to the event itself where coalescing isn't supported */
  const samplesOf = (e: PointerEvent): PointerEvent[] => {
    const coalesced = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    return coalesced.length ? coalesced : [e];
  };

  const onPointerDown = (e: PointerEvent): void => {
    const world = opts.getWorld();
    if (!world || world.state !== 'playing') return;
    const vs = opts.getViewScale();
    const { x, y } = toLogical(e.clientX, e.clientY, vs);
    const radius = e.pointerType === 'pen' ? RADIUS_PEN : RADIUS_TOUCH;
    const plane = planeAt(world, x, y, radius);
    if (!plane) {
      // empty sky: nothing to route, so the gesture becomes ink instead
      if (!opts.onDoodlePoint) return;
      doodle = { pointerId: e.pointerId, filter: new OneEuroFilter2D() };
      canvas.setPointerCapture(e.pointerId);
      opts.onDoodleStart?.();
      const p = doodle.filter.filter(x, y, e.timeStamp / 1000);
      opts.onDoodlePoint(p);
      return;
    }
    drag = { plane, stroke: new InkStroke(), pointerId: e.pointerId, ink: null };
    canvas.setPointerCapture(e.pointerId);
    opts.onDragChange?.(plane.id);
  };

  // `tMs` is the event's own timestamp (ms since navigation start) — real
  // hardware time per sample, not wall-clock — fed to the stroke's 1€ filter so
  // its speed estimate is correct even across a burst of coalesced events.
  const addSample = (d: Drag, clientX: number, clientY: number, vs: number, tMs: number): void => {
    const raw = toLogical(clientX, clientY, vs);
    d.stroke.push(raw.x, raw.y, tMs / 1000);
    if (!opts.onInkPoint) return;
    const prev = d.ink;
    const gap = prev ? Math.hypot(raw.x - prev.x, raw.y - prev.y) : Infinity;
    if (gap < MIN_SPACING) return;
    // speed over the gap just crossed; the first sample is still, and a burst of
    // coalesced events never divides by less than ~one frame
    const speed = prev ? gap / (Math.max(8, tMs - prev.tMs) / 1000) : 0;
    d.ink = { x: raw.x, y: raw.y, tMs };
    opts.onInkPoint(raw, speed);
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (doodle && e.pointerId === doodle.pointerId) {
      const vs = opts.getViewScale();
      for (const ev of samplesOf(e)) {
        const raw = toLogical(ev.clientX, ev.clientY, vs);
        opts.onDoodlePoint?.(doodle.filter.filter(raw.x, raw.y, ev.timeStamp / 1000));
      }
      return;
    }
    if (!drag || e.pointerId !== drag.pointerId) return;
    const world = opts.getWorld();
    if (!world || world.state !== 'playing') return;
    const vs = opts.getViewScale();
    for (const ev of samplesOf(e)) addSample(drag, ev.clientX, ev.clientY, vs, ev.timeStamp);
    // a mere tap leaves the current path intact — only once a real drag
    // begins (>=3 points) does it start previewing a replacement. The preview
    // is the *smoothed* stroke, the same geometry `commit` will hand over, so
    // the line never changes shape under the finger at the moment of release.
    if (drag.stroke.length >= 3) previewPath(drag.plane, drag.stroke.points());
  };

  const endDrag = (e: PointerEvent, commit: boolean): void => {
    if (doodle && e.pointerId === doodle.pointerId) {
      doodle = null;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      return; // the ink is already drawn and already fading; there is nothing to commit
    }
    if (!drag || e.pointerId !== drag.pointerId) return;
    const { plane, stroke } = drag;
    drag = null;
    opts.onDragChange?.(null);
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    if (!commit) { opts.onInkEnd?.('cancel'); return; } // pointercancel: drop the gesture entirely, like the prototype
    const world = opts.getWorld();
    if (!world) { opts.onInkEnd?.('cancel'); return; }
    if (stroke.length < 3) { toggleHold(world, plane); opts.onInkEnd?.('hold'); opts.onCommand?.('hold', plane); return; }
    // `commit` ends the stroke on the true (unfiltered) release position, so
    // filter lag on a fast flick can never cost a runway/pad snap at the one
    // moment it matters. `smooth: false`: the ink is already evenly spaced and
    // smoothed (src/input/ink.ts), and a second pass in the sim would quietly
    // make the committed path differ from the previewed one.
    const release = toLogical(e.clientX, e.clientY, opts.getViewScale());
    const result = assignPath(world, plane, stroke.commit(release), { smooth: false });
    opts.onInkEnd?.(result);
    opts.onCommand?.('path', plane);
  };

  const onPointerUp = (e: PointerEvent): void => endDrag(e, true);
  const onPointerCancel = (e: PointerEvent): void => endDrag(e, false);
  // stop iOS Safari's pinch-zoom / long-press callouts from hijacking the drag
  const onTouchStart = (e: TouchEvent): void => { e.preventDefault(); };
  const onGestureStart = (e: Event): void => { e.preventDefault(); };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerCancel);
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('gesturestart', onGestureStart);

  return () => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    canvas.removeEventListener('touchstart', onTouchStart);
    canvas.removeEventListener('gesturestart', onGestureStart);
  };
}
